import { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CreateFavoriteBid, DeleteFavoriteBid } from "@/services/Bid";

import { TabMenu } from "@/types/Shared/tab";

import ContentLayout from "@/layouts/ContentLayout";

import { ProjectType } from "@/consts/Bid";

import { showToast } from "@/utils/toastUtils";

import CommonButton from "@/components/Common/Button";
import CommonImg from "@/components/Common/Img";
import CommonTab from "@/components/Common/Tab";

import ShareIcon from "@/assets/images/share-icon.png";
import FavoriteIconFalse from "@/assets/images/favorite-icon-false.png";
import FavoriteIconTrue from "@/assets/images/favorite-icon-true.png";
import BidDetailIcon from "@/assets/images/Tab/bid-detail-icon.png";
import BidDetailIconActive from "@/assets/images/Tab/bid-detail-icon-active.png";
import BidAnalyticsIcon from "@/assets/images/Tab/bid-analytics-icon.png";
import BidAnalyticsIconActive from "@/assets/images/Tab/bid-analytics-icon-active.png";
import BidSimilarIcon from "@/assets/images/Tab/bid-similar-icon.png";
import BidSimilarIconActive from "@/assets/images/Tab/bid-similar-icon-active.png";
import BidLinkIcon from "@/assets/images/BidDetail/link-icon.png";

type Props = {
  projectType: string;
  projectId: string;
  projectTitle: string;
  isStarred: boolean;
  bidLink?: string;
  children: ReactNode;
  onRefresh: () => void;
};
export default function BidDetailHeader({
  projectType,
  projectId,
  projectTitle,
  isStarred,
  bidLink,
  children,
  onRefresh,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const isOriginal = projectType === ProjectType.Original;
  const defaultTabData: TabMenu[] = [
    {
      label: "공고 상세",
      path: `/bid/domestic/detail/${projectType}/${projectId}`,
      icon: BidDetailIcon,
      activeIcon: BidDetailIconActive,
    },
    {
      label: "규격서 분석",
      path: `/bid/domestic/detail/${projectType}/${projectId}/spec-analysis`,
      icon: BidAnalyticsIcon,
      activeIcon: BidAnalyticsIconActive,
    },
  ];
  const forOriginalTabData: TabMenu[] = [
    {
      label: "유사 사업 분석",
      path: `/bid/domestic/similar/${projectType}/${projectId}`,
      icon: BidSimilarIcon,
      activeIcon: BidSimilarIconActive,
    },
  ];
  // 공고 원문 링크는 일반공고/사전규격 공통으로, 원문 URL이 있을 때만 노출
  const bidLinkTabData: TabMenu[] = bidLink
    ? [
        {
          label: "공고 원문",
          path: bidLink,
          icon: BidLinkIcon,
          activeIcon: "",
          isLink: true,
        },
      ]
    : [];
  const tabData: TabMenu[] = (
    isOriginal ? defaultTabData.concat(forOriginalTabData) : defaultTabData
  ).concat(bidLinkTabData);
  const handleClickFavorite = async () => {
    try {
      if (isStarred) {
        const result = await DeleteFavoriteBid(projectType, projectId);
        if (result.data.success) {
          onRefresh();
        } else {
          alert(result.data.error.content);
        }
      } else {
        const result = await CreateFavoriteBid(projectType, projectId);
        if (result.data.success) {
          onRefresh();
        } else {
          alert(result.data.error.content);
        }
      }
    } catch (e) {
    } finally {
    }
  };

  const handleCopyLink = async () => {
    // 잘못된 타입 토큰(bid)이 포함된 경로가 공유 링크로 퍼지지 않도록 canonical 경로로 교정
    const copyPath = location.pathname.replace(
      /^\/bid\/domestic\/(detail|similar)\/bid\//,
      "/bid/domestic/$1/original/"
    );
    const copyText = `${process.env.REACT_APP_URL}${copyPath}`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          showToast({ message: "주소가 복사되었습니다." });
        })
        .catch((err) => {
          console.error(">> ", err);
        });
    } else {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = copyText;
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.position = "fixed";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const copyExec = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (copyExec) {
          showToast({ message: "주소가 복사되었습니다." });
        } else {
          console.error(">> error");
        }
      } catch (err) {
        console.log(">> error", err);
      }
    }
  };

  return (
    <ContentLayout>
      <div>
        <CommonButton
          label="뒤로가기"
          className="h-[25px] px-[12px] py-[4px] bg-white border-[1px] border-gray-D rounded-2xl flex items-center cursor-pointer text-sm text-gray-A font-normal"
          onClick={() => navigate(-1)}
        />
      </div>
      <div className="flex gap-[12px]">
        <div className="text-lg font-bold">{projectTitle}</div>
        <div
          className="flex gap-[4px] items-center text-sm text-gray-A font-semibold cursor-pointer"
          onClick={() => {
            handleClickFavorite();
          }}
        >
          {isStarred ? (
            <>
              <CommonImg
                src={FavoriteIconTrue}
                alt="Favorite"
                className="size-[20px]"
              />
              관심 공고 해제
            </>
          ) : (
            <>
              <CommonImg
                src={FavoriteIconFalse}
                alt="Favorite"
                className="size-[20px]"
              />
              관심 공고 추가
            </>
          )}
        </div>
        <div
          className="flex gap-[4px] items-center text-sm text-gray-A font-semibold cursor-pointer"
          onClick={() => {
            handleCopyLink();
          }}
        >
          <CommonImg src={ShareIcon} alt="Share" className="size-[16px]" />
          공유하기
        </div>
      </div>
      <div>
        <CommonTab variants="button" data={tabData} />
        {children}
      </div>
    </ContentLayout>
  );
}
